import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import { usePageMeta } from '@/hooks/usePageMeta';

const hotKeywords = ['家庭', '婚姻', '亲情', '都市', '悬疑', '伦理电影'];

const sortOptions = [
  { key: 'relevance', label: '相关度' },
  { key: 'hot', label: '按热度' },
  { key: 'newest', label: '按更新时间' },
];

const catOptions = [
  { key: 'all', label: '全部分类' },
  { key: 'traditional', label: '传统伦理剧' },
  { key: 'modern', label: '现代伦理剧' },
  { key: 'film', label: '伦理电影' },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'relevance';
  const cat = params.get('cat') || 'all';

  const [input, setInput] = useState(q);

  usePageMeta({
    title: q ? `搜索“${q}” - 韩国伦理影视推荐` : '搜索韩国伦理影视 - 韩国伦理',
    description: q
      ? `在韩国伦理站内搜索“${q}”的相关影视作品，快速找到你感兴趣的韩国伦理剧与伦理电影。`
      : '输入关键词，快速搜索韩国伦理影视作品，支持按类别与热度筛选。',
    keywords: '韩国伦理搜索,韩国伦理影视,伦理影视推荐',
  });

  useEffect(() => {
    setInput(q);
  }, [q]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (input === q) return;
      const next = new URLSearchParams(params);
      if (input.trim()) next.set('q', input.trim());
      else next.delete('q');
      next.delete('sort');
      next.delete('cat');
      setParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const results = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    if (!keyword) return [];
    const list = movies.filter((m) => {
      if (cat !== 'all' && m.category !== cat) return false;
      return (
        m.title.toLowerCase().includes(keyword) ||
        m.summary.toLowerCase().includes(keyword) ||
        m.genre.toLowerCase().includes(keyword) ||
        m.categoryLabel.toLowerCase().includes(keyword) ||
        m.director.toLowerCase().includes(keyword) ||
        m.cast.some((c) => c.toLowerCase().includes(keyword))
      );
    });
    if (sort === 'hot') list.sort((a, b) => b.views - a.views);
    else if (sort === 'newest') list.sort((a, b) => b.year - a.year);
    else list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [q, sort, cat]);

  const hot = useMemo(() => [...movies].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const setParam = (key: string, value: string, def: string) => {
    const next = new URLSearchParams(params);
    if (value === def) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="pt-16">
      <div className="w-full px-4 md:px-8 py-10 md:py-14 bg-primary-950">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">搜索韩国伦理影视</h1>
          <p className="mt-3 text-sm text-primary-200/85">输入影视名称、主演、题材或分类关键词，快速找到你想看的作品。</p>
          <div className="mt-6 flex items-center gap-3 max-w-2xl mx-auto">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                type="search"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：家庭的阴影、家庭、亲情、伦理电影"
                className="w-full pl-11 pr-4 py-3.5 rounded-md bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:ring-2 focus:ring-accent-400"
                aria-label="搜索关键词"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-primary-300/80">热门搜索：</span>
            {hotKeywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => setInput(kw)}
                className="px-3 py-1 rounded-full text-xs bg-background-50/10 text-background-100 hover:bg-accent-500 hover:text-background-50 transition-colors whitespace-nowrap"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-5xl mx-auto">
          {q ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <p className="text-sm text-foreground-600">
                  关键词 <span className="font-semibold text-primary-600">“{q}”</span> 共找到{' '}
                  <span className="font-semibold text-primary-600">{results.length}</span> 个结果
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={cat}
                    onChange={(e) => setParam('cat', e.target.value, 'all')}
                    className="px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 outline-none focus:ring-1 focus:ring-primary-400"
                    aria-label="按类别筛选"
                  >
                    {catOptions.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                  <select
                    value={sort}
                    onChange={(e) => setParam('sort', e.target.value, 'relevance')}
                    className="px-3 py-2 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 outline-none focus:ring-1 focus:ring-primary-400"
                    aria-label="排序方式"
                  >
                    {sortOptions.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {results.length > 0 ? (
                <div className="space-y-4">
                  {results.map((m) => (
                    <MovieCard key={m.id} movie={m} variant="list" />
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center rounded-lg bg-background-100 border border-background-200">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
                    <i className="ri-search-eye-line" />
                  </div>
                  <p className="mt-4 text-foreground-700">没有找到与“{q}”相关的影视作品</p>
                  <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门推荐。</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-10">
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500 text-2xl">
                <i className="ri-film-line" />
              </div>
              <p className="mt-4 text-foreground-700">输入关键词开始搜索</p>
              <p className="mt-2 text-sm text-foreground-500">也可以先从下面的热门作品中浏览。</p>
            </div>
          )}

          {/* Hot recommendations */}
          <div className="mt-12">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">热门推荐</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {hot.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            <div className="mt-6 text-center">
              <Link
                to="/category"
                className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                浏览全部影视分类 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}